'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { GraduationCap } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface Progress { posts: number; days: number; unlocked: boolean }
interface Rule { minPosts: number; minDays: number }

// Own-profile nudge toward /akademija: "još N objava do video lekcija". Hidden for guests and
// once the lessons are unlocked (rule lives in src/lib/academy.ts).
export default function AcademyBanner() {
  const { session } = useAuth() as { session: any };
  const [state, setState] = useState<{ progress: Progress; rule: Rule } | null>(null);

  useEffect(() => {
    if (!session?.access_token) return;
    let alive = true;
    fetch('/api/academy', { headers: { Authorization: `Bearer ${session.access_token}` }, cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => { if (alive && d?.progress) setState({ progress: d.progress, rule: d.rule }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [session?.access_token]);

  if (!state || state.progress.unlocked) return null;
  const { progress: p, rule } = state;
  const postsLeft = Math.max(0, rule.minPosts - p.posts);
  const daysLeft = Math.max(0, rule.minDays - p.days);
  const pct = Math.round((Math.min(p.posts, rule.minPosts) / rule.minPosts) * 100);
  const need = postsLeft > 0
    ? `Još ${postsLeft} ${objava(postsLeft)} sa grafikom`
    : `Još objava u ${daysLeft} ${daysLeft === 1 ? 'dan' : 'dana'}`;

  return (
    <Link
      href="/akademija"
      className="block rounded-xl p-4 animate-fade-in"
      style={{ background: 'var(--accent-muted)', border: '1px solid var(--accent)' }}
    >
      <div className="flex items-center gap-3">
        <GraduationCap size={22} style={{ color: 'var(--accent)' }} className="shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
            {need} do 3 video lekcije iz tehničke analize
          </div>
          <div className="h-1.5 rounded-full mt-2 overflow-hidden" style={{ background: 'var(--bg-sunken)' }}>
            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'var(--accent)' }} />
          </div>
        </div>
        <span className="text-xs font-semibold shrink-0 font-mono-nums" style={{ color: 'var(--accent)' }}>
          {Math.min(p.posts, rule.minPosts)}/{rule.minPosts}
        </span>
      </div>
    </Link>
  );
}

// 1 objava · 2–4 objave · 5+ objava (11–14 → objava)
function objava(n: number) {
  const d = n % 10, h = n % 100;
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return 'objave';
  return 'objava';
}
